import { useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useChatMessages } from "@/hooks/use-chat-messages";
import { useChatActions } from "@/hooks/use-chat-actions";
import { useChatTyping } from "@/hooks/use-chat-typing";
import { usePresence, getPresenceStatus } from "@/hooks/use-presence";
import { PresenceDot } from "@/components/shared/PresenceDot";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { ReadReceipts } from "./ReadReceipts";
import { computeReceiptsByMessage } from "./read-receipts";
import type { ChatRoom } from "@/hooks/use-chat-rooms";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * VIB-728 B: Online-status under navn i ChatWindow header.
 * DM: dot + tekst (Aktiv nå / Inaktiv / Offline).
 * Gruppe: "{N} av {M} aktive".
 */
function PresenceLine({
  room,
  currentUserId,
  presenceMap,
}: {
  room: ChatRoom;
  currentUserId: string;
  presenceMap: Map<string, "online" | "idle" | "offline">;
}) {
  if (room.type === "direct") {
    const otherUser =
      room.otherUser ?? room.members.find((m) => m.user_id !== currentUserId);
    if (!otherUser) return null;
    const status = getPresenceStatus(presenceMap, otherUser.user_id);
    const label =
      status === "online"
        ? "Aktiv nå"
        : status === "idle"
          ? "Inaktiv"
          : "Offline";
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <PresenceDot userId={otherUser.user_id} size="sm" />
        <span>{label}</span>
      </div>
    );
  }
  // Gruppe
  const others = room.members.filter((m) => m.user_id !== currentUserId);
  const activeCount = others.filter((m) => {
    const s = getPresenceStatus(presenceMap, m.user_id);
    return s === "online" || s === "idle";
  }).length;
  return (
    <div className="text-[11px] text-muted-foreground">
      {activeCount} av {others.length} aktive
    </div>
  );
}

function roomTitle(room: ChatRoom): string {
  if (room.type !== "direct") return room.name ?? "Gruppe";
  return room.otherUser?.full_name ?? "Direktemelding";
}

export function ChatWindow({
  room,
  currentUserId,
  currentUserName,
  onBack,
}: {
  room: ChatRoom;
  currentUserId: string;
  currentUserName: string | null;
  onBack: () => void;
}) {
  const { messages, isLoading, hasMore, isFetchingMore, loadMore } = useChatMessages(room.id);
  const { sendMessage, markAsRead } = useChatActions(currentUserId);
  const { typingUsers, setTyping } = useChatTyping({
    roomId: room.id,
    userId: currentUserId,
    fullName: currentUserName,
  });
  const presenceMap = usePresence(currentUserId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageId = messages[messages.length - 1]?.id;

  const receiptsByMessage = useMemo(
    () => computeReceiptsByMessage(messages, room.members, currentUserId),
    [messages, room.members, currentUserId],
  );

  // Auto-scroll til bunn når nye meldinger kommer
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [lastMessageId]);

  // Marker som lest når vinduet åpnes og ved nye meldinger
  useEffect(() => {
    markAsRead.mutate(room.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id, lastMessageId]);

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-card shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <SrIcon name="arrow-left" className="w-4 h-4"  />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate flex items-center gap-1.5">
            {room.type !== "direct" && <SrIcon name="users" className="w-3.5 h-3.5 text-muted-foreground shrink-0"  />}
            <span className="truncate">{roomTitle(room)}</span>
          </div>
          <PresenceLine
            room={room}
            currentUserId={currentUserId}
            presenceMap={presenceMap}
          />
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {hasMore && (
          <div className="flex justify-center pb-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadMore()}
              disabled={isFetchingMore}
              className="text-xs"
            >
              {isFetchingMore ? <SrIcon name="loader2" className="w-3 h-3 mr-1.5 animate-spin"  /> : null}
              Last eldre meldinger
            </Button>
          </div>
        )}
        {isLoading && (
          <div className="flex justify-center py-4">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-8">
            Ingen meldinger ennå. Skriv den første!
          </p>
        )}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const isSelf = m.sender_id === currentUserId;
          // Vis avatar/navn hvis avsender endrer seg
          const showAvatar = !prev || prev.sender_id !== m.sender_id;
          const readers = receiptsByMessage.get(m.id) ?? [];
          return (
            <div key={m.id}>
              <ChatMessage message={m} isSelf={isSelf} showAvatar={showAvatar} />
              {readers.length > 0 && <ReadReceipts readers={readers} />}
            </div>
          );
        })}
      </div>

      {typingUsers.length > 0 && (
        <div className="px-4 py-1 text-[11px] italic text-muted-foreground bg-muted/30 border-t">
          {formatTypingLabel(typingUsers.map((u) => u.full_name))}
        </div>
      )}

      <ChatInput
        onTypingChange={setTyping}
        onSend={async (body, file) => {
          await sendMessage.mutateAsync({ roomId: room.id, senderId: currentUserId, body, file });
        }}
      />
    </div>
  );
}

function formatTypingLabel(names: string[]): string {
  const real = names.filter((n) => n && n.trim().length > 0).map((n) => n.split(" ")[0]);
  if (real.length === 0) return "skriver...";
  if (real.length === 1) return `${real[0]} skriver...`;
  if (real.length === 2) return `${real[0]} og ${real[1]} skriver...`;
  return `${real[0]}, ${real[1]} og ${real.length - 2} til skriver...`;
}

export default ChatWindow;
